import { memo, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import { Box, Chip, CircularProgress, Collapse, GlobalStyles, IconButton, Link, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PrintIcon from '@mui/icons-material/Print';
import AiVisualization from './AiVisualization';
import { CHART_FENCE, AiChatMessage as AiChatMessageType } from './sseProtocol';
import { prepareStreamingContent, splitContentSegments } from './streamFormat';
import { PRINT_BODY_CLASS, PRINT_TARGET_CLASS, printMessage } from './printExport';

// @media print: hide everything, then re-show the marked message. visibility
// (not display) so painted chart canvases survive the print. GlobalStyles
// dedupes by content, so this is effectively one style tag.
export const AiPrintStyles = () => (
  <GlobalStyles
    styles={{
      [`@media print`]: {
        [`body.${PRINT_BODY_CLASS} *`]: { visibility: 'hidden' },
        [`body.${PRINT_BODY_CLASS} .${PRINT_TARGET_CLASS}, body.${PRINT_BODY_CLASS} .${PRINT_TARGET_CLASS} *`]:
          { visibility: 'visible' },
        [`body.${PRINT_BODY_CLASS} .${PRINT_TARGET_CLASS}`]: {
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          maxWidth: '100%',
          border: 'none',
          boxShadow: 'none',
        },
        [`body.${PRINT_BODY_CLASS} .${PRINT_TARGET_CLASS} .stackiq-message-actions`]: {
          display: 'none',
        },
        [`body.${PRINT_BODY_CLASS} .${PRINT_TARGET_CLASS} .stackiq-working-disclosure`]: {
          display: 'none',
        },
        [`body.${PRINT_BODY_CLASS} .${PRINT_TARGET_CLASS} .stackiq-chart-actions`]: {
          display: 'none',
        },
      },
    }}
  />
);

// Matches the <!--title:...--> comment the model must open with. Prefer the
// last match (HemIQ parity). Kept out of the rendered markdown.
export const TITLE_COMMENT_RE = /<!--\s*title:\s*([^-][^>]*?)\s*-->/gi;

export const extractTitle = (content: string): { title: string | null; body: string } => {
  let title: string | null = null;
  const body = content.replace(TITLE_COMMENT_RE, (_, captured) => {
    title = String(captured).trim();
    return '';
  });
  return { title, body: body.trim() };
};

const markdownComponents = {
  code: ({ inline, className, children }: any) => {
    const language = /language-(\S+)/.exec(className || '')?.[1];
    const value = String(children ?? '');
    // Completed chart fences are normally pre-split into memoized segments;
    // this branch is a fallback (e.g. fences inside blockquotes).
    if (!inline && language === CHART_FENCE) {
      return <AiVisualization raw={value} />;
    }
    if (inline) {
      return (
        <code style={{ background: '#eef1f5', padding: '1px 5px', borderRadius: 4, fontSize: '0.88em' }}>
          {children}
        </code>
      );
    }
    return (
      <pre style={{ background: '#f5f7fa', padding: 12, borderRadius: 8, overflowX: 'auto' }}>
        <code>{children}</code>
      </pre>
    );
  },
  table: ({ children }: any) => (
    <Box component="div" sx={{ overflowX: 'auto', my: 1 }}>
      <table className="ai-md-table">{children}</table>
    </Box>
  ),
};

// Completed charts render once and stay stable across streaming deltas
// (HemIQ's MemoizedChartBlock pattern).
const MemoizedChart = memo(({ rawValue }: { rawValue: string }) => <AiVisualization raw={rawValue} />);

const MarkdownSegments = ({ content }: { content: string }) => (
  <>
    {splitContentSegments(content).map((segment) =>
      segment.type === 'chart' ? (
        <MemoizedChart key={segment.key} rawValue={segment.rawValue} />
      ) : (
        <ReactMarkdown key={segment.key} remarkPlugins={[gfm]} components={markdownComponents}>
          {segment.content}
        </ReactMarkdown>
      )
    )}
  </>
);

// Animated placeholder shown while an unclosed code/chart fence is streaming
// in — the raw JSON never reaches the screen (HemIQ parity).
const OpenBlockPlaceholder = ({ label }: { label: string }) => (
  <Box
    data-testid="ai-open-block-placeholder"
    display="flex"
    alignItems="center"
    gap={1}
    sx={{ border: '1px dashed #c5d5e0', borderRadius: 2, px: 1.5, py: 1.25, my: 1, background: '#f8fbfd' }}
  >
    <CircularProgress size={13} sx={{ color: '#0088CF' }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const AiMessage = ({ message, streaming }: { message: AiChatMessageType; streaming?: boolean }) => {
  const [copied, setCopied] = useState(false);
  const [showWorking, setShowWorking] = useState(false);
  const { title, body } = useMemo(() => extractTitle(message.content || ''), [message.content]);
  const isUser = message.role === 'user';
  const messageRef = useRef<HTMLDivElement>(null);

  // Streaming pipeline: strip half-streamed title comments, hold back
  // unclosed fences behind a placeholder.
  const streamed = useMemo(
    () => (streaming && !isUser ? prepareStreamingContent(body) : null),
    [streaming, isUser, body]
  );

  const print = () => {
    if (messageRef.current) printMessage(messageRef.current, title);
  };

  const copy = async () => {
    try {
      // Chart JSON is noise outside the app — copy the prose only.
      const stripped = body
        .replace(new RegExp('```' + CHART_FENCE + '[\\s\\S]*?```', 'g'), '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      await navigator.clipboard.writeText(stripped);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  if (isUser) {
    return (
      <Box display="flex" justifyContent="flex-end" my={1}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #0088CF, #00B7B2)',
            color: '#fff',
            borderRadius: '14px 14px 3px 14px',
            px: 1.75,
            py: 1,
            maxWidth: '82%',
            whiteSpace: 'pre-wrap',
            fontSize: 14.5,
            boxShadow: '0 1px 2px rgba(16, 42, 67, 0.12)',
          }}
        >
          {message.attachments && message.attachments.length > 0 ? (
            <Box display="flex" gap={0.5} flexWrap="wrap" mb={message.content ? 0.5 : 0}>
              {message.attachments.map((attachment, index) => (
                <Chip
                  key={`${attachment.name}-${index}`}
                  label={attachment.name}
                  size="small"
                  sx={{ background: 'rgba(255,255,255,0.25)', color: '#fff' }}
                />
              ))}
            </Box>
          ) : null}
          {message.content}
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="flex-start" my={1}>
      <AiPrintStyles />
      <Box
        ref={messageRef}
        sx={{
          background: message.error ? '#fef2f2' : '#fff',
          border: message.error ? '1px solid #fecaca' : '1px solid #e8edf2',
          borderRadius: '14px 14px 14px 3px',
          px: 2,
          py: 1.25,
          maxWidth: '92%',
          minWidth: 0,
          fontSize: 14.5,
          boxShadow: '0 1px 2px rgba(16, 42, 67, 0.06)',
          '& p': { my: 0.75 },
          '& ul, & ol': { my: 0.5, pl: 2.5 },
          '& .ai-md-table': {
            borderCollapse: 'collapse',
            '& th, & td': { border: '1px solid #e4e7eb', padding: '4px 8px', fontSize: 13.5 },
            '& th': { background: '#f0f2f5' },
          },
        }}
      >
        {/* Floated ahead of the content so the first line of text wraps
            around the buttons instead of running underneath them. */}
        {!message.error && !streaming && body ? (
          <Box
            className="stackiq-message-actions"
            sx={{ float: 'right', ml: 1, mb: 0.5, mt: -0.25, mr: -1, whiteSpace: 'nowrap' }}
          >
            <Tooltip title={copied ? 'Copied' : 'Copy'}>
              <IconButton size="small" onClick={copy} aria-label="Copy message">
                {copied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Print / save as PDF">
              <IconButton size="small" onClick={print} aria-label="Print message">
                <PrintIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

        {message.error ? (
          <Typography color="error" fontSize={14.5}>
            {message.content}
          </Typography>
        ) : streamed ? (
          <>
            {streamed.renderable ? <MarkdownSegments content={streamed.renderable} /> : null}
            {streamed.openBlockLabel ? (
              <OpenBlockPlaceholder label={streamed.openBlockLabel} />
            ) : (
              <Typography
                component="span"
                sx={{
                  color: '#0088CF',
                  animation: 'stackiq-blink 1s step-end infinite',
                  '@keyframes stackiq-blink': { '50%': { opacity: 0 } },
                }}
              >
                ▍
              </Typography>
            )}
          </>
        ) : (
          <MarkdownSegments content={body} />
        )}

        {message.truncated ? (
          <Typography variant="caption" color="text.secondary" display="block">
            (response was cut short by the length limit)
          </Typography>
        ) : null}
        {message.stopped ? (
          <Typography variant="caption" color="text.secondary" display="block">
            (stopped)
          </Typography>
        ) : null}

        {/* The same working trail admins see — which tools ran, with what
            request parameters. */}
        {!streaming && !message.error && message.queries && message.queries.length > 0 ? (
          <Box className="stackiq-working-disclosure" mt={0.5}>
            <Link
              component="button"
              variant="caption"
              color="text.secondary"
              underline="hover"
              onClick={() => setShowWorking(!showWorking)}
            >
              {showWorking
                ? 'Hide working'
                : `Show working (${message.queries.length} ${message.queries.length === 1 ? 'query' : 'queries'})`}
            </Link>
            <Collapse in={showWorking}>
              {message.queries.map((query, index) => (
                <Box key={index} mt={0.5}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#0f7ab5' }}>
                    {query.tool}
                  </Typography>
                  <pre
                    style={{
                      background: '#f0f2f5',
                      padding: 8,
                      borderRadius: 4,
                      fontSize: 11.5,
                      overflowX: 'auto',
                      margin: '2px 0 0',
                    }}
                  >
                    {JSON.stringify(query.input, null, 2)}
                  </pre>
                </Box>
              ))}
            </Collapse>
          </Box>
        ) : null}
        <Box sx={{ clear: 'both' }} />
      </Box>
    </Box>
  );
};

export default AiMessage;
